import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Alert, Box, Divider, Stack, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import T from 'T'
import HHDisplayMoney from 'components/HHDisplayMoney'
import { useLazyGetSurchargeRateQuery } from 'api/payment-gateway/paymentGateway'

// The rate endpoint returns the surcharge for a $100.00 reference amount; we scale it to the
// amount being paid for the breakdown.
const REFERENCE_AMOUNT_CENTS = 10000

/**
 * Surcharge disclosure for portal payments. When a saved-method token is available, it queries
 * the gateway for that card's actual rate and renders a Subtotal / Surcharge / Total breakdown —
 * the same approach used in the CRM. New-card payments get Fortis's native sale-mode breakdown,
 * so this is aimed at saved cards (whose stored token never re-renders the Fortis iframe).
 * Debit / ACH tokens come back surcharge_applied=false, so only the disclosure text shows.
 */
const SurchargePanel = ({ businessId = '', amountCents = 0, zip = '', token = '' }) => {
  const [getSurchargeRate, { data }] = useLazyGetSurchargeRateQuery()

  useEffect(() => {
    if (businessId && zip && token) {
      getSurchargeRate({ businessId, zip, token })
    }
  }, [businessId, zip, token, getSurchargeRate])

  const surchargeRate = data?.surchargeRate
  const referenceSurchargeCents = data?.surchargeAmountCents

  const breakdown = useMemo(() => {
    if (!data?.surchargeApplied || !referenceSurchargeCents || amountCents <= 0) return null
    const surchargeCents = Math.round((amountCents * referenceSurchargeCents) / REFERENCE_AMOUNT_CENTS)
    return { subtotalCents: amountCents, surchargeCents, totalCents: amountCents + surchargeCents }
  }, [data?.surchargeApplied, referenceSurchargeCents, amountCents])

  // Lead with the rate when we know it (e.g. "A 3.00% surcharge applies…"), else generic copy.
  const ratePrefix = surchargeRate ? `A ${surchargeRate}% surcharge applies to credit-card purchases. ` : ''

  return (
    <Alert
      severity="info"
      variant="outlined"
      icon={<InfoOutlinedIcon fontSize="small" />}
      sx={{ alignItems: 'flex-start', borderRadius: 2, width: '100%', '& .MuiAlert-message': { width: '100%', py: 0.25 } }}
    >
      <Stack spacing={breakdown ? 1.25 : 0}>
        <Typography variant="caption" color="text.secondary" lineHeight={1.5}>
          {`${ratePrefix}${T.TRANSACTION_SURCHARGE_DESCRIPTION}`}
        </Typography>
        {breakdown && (
          <>
            <Divider flexItem />
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  {T.SUBTOTAL}
                </Typography>
                <Typography variant="body2">
                  <HHDisplayMoney value={breakdown.subtotalCents} />
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  {T.SURCHARGE}
                </Typography>
                <Typography variant="body2">
                  <HHDisplayMoney value={breakdown.surchargeCents} />
                </Typography>
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mt={0.5}
                pt={0.5}
                sx={{ borderTop: '1px solid', borderColor: 'divider' }}
              >
                <Typography variant="body2" fontWeight={700}>
                  {T.TOTAL}
                </Typography>
                <Typography variant="body2" fontWeight={700} component="span">
                  <HHDisplayMoney value={breakdown.totalCents} />
                </Typography>
              </Stack>
            </Box>
          </>
        )}
      </Stack>
    </Alert>
  )
}

SurchargePanel.propTypes = {
  businessId: PropTypes.string,
  amountCents: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  zip: PropTypes.string,
  token: PropTypes.string,
}

export default SurchargePanel
